import ApproverNames from '../components/ApproverNames'
import { useState, useEffect } from 'react'
import { supabase } from '../supabaseClient'
import Fixtures from './Fixtures'
import Squad from './Squad'
import Dashboard from './Dashboard'
import Admin from './Admin'
import MatchTracker from './MatchTracker'
import Logo from '../components/Logo'

export default function Main({ session, profile: initialProfile, onProfileUpdate }) {
const [tab, setTabState] = useState(() => {
    try {
      const saved = localStorage.getItem('gaa_active_tab')
      return saved || 'fixtures'
    } catch (e) {
      return 'fixtures'
    }
  })
  function setTab(newTab) {
    setTabState(newTab)
    try { localStorage.setItem('gaa_active_tab', newTab) } catch (e) { /* ignore storage errors */ }
  }
  const [profile, setProfile] = useState(initialProfile)
  const [activeMatch, setActiveMatchState] = useState(null)
  const [restoringMatch, setRestoringMatch] = useState(true)

  function setActiveMatch(fixture) {
    setActiveMatchState(fixture)
    try {
      if (fixture) localStorage.setItem('gaa_active_match_id', fixture.id)
      else localStorage.removeItem('gaa_active_match_id')
    } catch (e) { /* ignore storage errors */ }
  }

  // Restore the open match after a reload (e.g. mobile Safari backgrounding)
  useEffect(() => {
    async function restoreMatch() {
      let savedId = null
      try { savedId = localStorage.getItem('gaa_active_match_id') } catch (e) { /* ignore */ }
      if (savedId) {
        const { data } = await supabase.from('fixtures')
          .select('*, squads(name, age_group, slug)')
          .eq('id', savedId).maybeSingle()
        if (data && data.status !== 'completed') {
          setActiveMatchState(data)
        } else {
          try { localStorage.removeItem('gaa_active_match_id') } catch (e) { /* ignore */ }
        }
      }
      setRestoringMatch(false)
    }
    restoreMatch()
  }, [])
  const [showLogout, setShowLogout] = useState(false)
  const [editName, setEditName] = useState('')
  const [editEmail, setEditEmail] = useState('')
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveMessage, setSaveMessage] = useState('')
  const [saveError, setSaveError] = useState('')

  useEffect(() => { setProfile(initialProfile) }, [initialProfile])

  const isClubAdmin = profile?.role === 'club_admin'
  const isAdminRole = profile?.role === 'club_admin' || profile?.role === 'squad_admin'

  async function handleLogout() {
    await supabase.auth.signOut()
  }

  function startEditing() {
    setEditName(profile?.full_name || '')
    setEditEmail(session?.user?.email || '')
    setEditing(true)
    setSaveMessage('')
    setSaveError('')
  }

  async function saveProfile() {
    if (!editName.trim()) { setSaveError('Name cannot be empty.'); return }
    if (!editEmail.trim()) { setSaveError('Email cannot be empty.'); return }
    setSaving(true); setSaveError(''); setSaveMessage('')

    try {
      // Update name in profiles table
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ full_name: editName.trim() })
        .eq('id', session.user.id)

      if (profileError) { setSaveError('Error updating name: ' + profileError.message); setSaving(false); return }

      // Update email in auth if changed
      if (editEmail.trim().toLowerCase() !== session.user.email) {
        const { error: emailError } = await supabase.auth.updateUser({
          email: editEmail.trim().toLowerCase()
        })
        if (emailError) { setSaveError('Error updating email: ' + emailError.message); setSaving(false); return }
        setSaveMessage('Name updated. A confirmation email has been sent to ' + editEmail + ' to verify your new address.')
      } else {
        setSaveMessage('Profile updated successfully.')
      }

      // Update local profile state
      const updatedProfile = { ...profile, full_name: editName.trim() }
      setProfile(updatedProfile)
      if (onProfileUpdate) onProfileUpdate(updatedProfile)
      setEditing(false)
    } catch (e) {
      setSaveError('Error: ' + e.message)
    }
    setSaving(false)
  }

  const navItems = [
    { id: 'fixtures', label: 'Fixtures', icon: '📅' },
    { id: 'squad', label: 'Squad', icon: '👥' },
    { id: 'dashboard', label: 'Dashboard', icon: '📊' },
...(isAdminRole ? [{ id: 'admin', label: 'Admin', icon: '⚙️' }] : []),
    { id: 'account', label: 'Account', icon: '👤' },
  ]

  if (restoringMatch) {
    return (
      <div className="app-shell" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <p style={{ color: '#888', fontSize: '14px' }}>Loading...</p>
      </div>
    )
  }

  if (activeMatch) {
    return (
      <div className="app-shell">
        <div style={{ flex: 1, overflowY: 'auto', paddingBottom: '70px' }}>
          <MatchTracker
            fixture={activeMatch}
            profile={profile}
            onBack={() => setActiveMatch(null)}
          />
        </div>
        <nav style={navStyle}>
          {navItems.map(item => (
            <button key={item.id}
              onClick={() => {
                if (item.id === 'account') { setActiveMatch(null); setShowLogout(true); setTab('fixtures') }
                else { setActiveMatch(null); setTab(item.id); setShowLogout(false) }
              }}
              style={{ ...navBtn, color: '#888' }}>
              <span style={{ fontSize: '20px' }}>{item.icon}</span>
              <span style={{ fontSize: '10px', marginTop: '2px' }}>{item.label}</span>
            </button>
          ))}
        </nav>
      </div>
    )
  }

  return (
    <div className="app-shell">
      <div style={{ flex: 1, overflowY: 'auto', paddingBottom: '70px' }}>

        {/* Account screen */}
        {showLogout && (
          <div>
            <div style={{ padding: '16px', borderBottom: '0.5px solid #e0e0e0' }}>
              <h2 style={{ fontSize: '17px', fontWeight: '500' }}>Account</h2>
            </div>
            <div style={{ padding: '20px 16px' }}>

              {saveMessage && (
                <div style={{ background: '#EAF3DE', border: '0.5px solid #3B6D11', borderRadius: '8px', padding: '10px 12px', marginBottom: '16px', fontSize: '13px', color: '#3B6D11' }}>
                  {saveMessage}
                  <button onClick={() => setSaveMessage('')} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', color: '#3B6D11' }}>✕</button>
                </div>
              )}

              {saveError && (
                <div style={{ background: '#FCEBEB', border: '0.5px solid #A32D2D', borderRadius: '8px', padding: '10px 12px', marginBottom: '16px', fontSize: '13px', color: '#A32D2D' }}>
                  {saveError}
                  <button onClick={() => setSaveError('')} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', color: '#A32D2D' }}>✕</button>
                </div>
              )}

              {/* Profile card */}
              <div style={{ background: '#f8f8f8', borderRadius: '12px', padding: '16px', marginBottom: '20px' }}>
                {editing ? (
                  <>
                    <p style={{ fontSize: '12px', color: '#888', marginBottom: '12px', fontWeight: '500' }}>EDIT PROFILE</p>
                    <input
                      style={inputStyle}
                      type="text"
                      placeholder="Full name"
                      value={editName}
                      onChange={e => setEditName(e.target.value)}
                    />
                    <input
                      style={inputStyle}
                      type="email"
                      placeholder="Email address"
                      value={editEmail}
                      onChange={e => setEditEmail(e.target.value)}
                    />
                    <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                      <button onClick={saveProfile} disabled={saving}
                        style={{ flex: 1, padding: '10px', background: '#185FA5', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', cursor: 'pointer', opacity: saving ? 0.7 : 1 }}>
                        {saving ? 'Saving...' : 'Save Changes'}
                      </button>
                      <button onClick={() => { setEditing(false); setSaveError('') }}
                        style={{ flex: 1, padding: '10px', background: 'white', color: '#888', border: '0.5px solid #ccc', borderRadius: '8px', fontSize: '13px', cursor: 'pointer' }}>
                        Cancel
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <p style={{ fontSize: '12px', color: '#888', fontWeight: '500' }}>PROFILE</p>
                      <button onClick={startEditing}
                        style={{ fontSize: '12px', padding: '4px 10px', borderRadius: '6px', border: '0.5px solid #185FA5', background: '#E6F1FB', color: '#185FA5', cursor: 'pointer' }}>
                        ✏️ Edit
                      </button>
                    </div>
                    <p style={{ fontSize: '15px', fontWeight: '500', color: '#333' }}>{profile?.full_name || 'Coach'}</p>
                    <p style={{ fontSize: '13px', color: '#888', marginTop: '4px' }}>{session?.user?.email}</p>
                    <p style={{ fontSize: '12px', color: '#aaa', marginTop: '4px' }}>
                      {profile?.role === 'club_admin' ? '⚙️ Club Admin' : profile?.role === 'squad_admin' ? '🏆 Squad Admin' : '👟 Coach'} · {profile?.clubs?.club_name || profile?.clubs?.name || ''}
                    </p>
                  </>
                )}
              </div>

              {/* Logo */}
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <Logo maxWidth="400px" style={{ margin: '0 auto' }} />
            </div>

              {/* Sign out */}
              <button onClick={handleLogout}
                style={{ display: 'block', width: '100%', padding: '14px', background: '#FCEBEB', color: '#A32D2D', border: '0.5px solid #A32D2D', borderRadius: '10px', fontSize: '15px', fontWeight: '500', cursor: 'pointer' }}>
                Sign Out
              </button>
            </div>
          </div>
        )}
      {/* Pending approval */}
      {!showLogout && profile?.approved === false && (
        <div style={{ padding: '40px 24px', textAlign: 'center', color: '#888' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
          <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#333', marginBottom: '8px' }}>Awaiting Approval</h2>
          <p style={{ fontSize: '14px', lineHeight: '1.6', marginBottom: '16px' }}>
            Your request to join is waiting for approval. You'll get access once an admin approves you.
          </p>
          <ApproverNames />
          <button onClick={() => supabase.auth.signOut()}
            style={{ padding: '12px 24px', background: '#FCEBEB', color: '#A32D2D', border: '0.5px solid #A32D2D', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' }}>
            Sign Out
          </button>
        </div>
      )}

      {/* No squad assigned yet — approved but no squad */}
      {!showLogout && profile?.approved === true && profile?.role === 'coach' && (!profile?.squadIds || profile.squadIds.length === 0) && (
          <div style={{ padding: '40px 24px', textAlign: 'center', color: '#888' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
            <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#333', marginBottom: '8px' }}>Awaiting Squad Access</h2>
            <p style={{ fontSize: '14px', lineHeight: '1.6', marginBottom: '24px' }}>
              Your account is approved but you haven't been assigned to a squad yet. Your squad admin will assign you shortly.
            </p>
            <p style={{ fontSize: '12px', color: '#aaa' }}>Contact your club admin if you think this is a mistake.</p>
          </div>
        )}

        {!showLogout && tab === 'fixtures' && (
          <Fixtures session={session} profile={profile} onTabChange={setTab} onOpenMatch={setActiveMatch} />
        )}
        {!showLogout && tab === 'squad' && <Squad session={session} profile={profile} />}
        {!showLogout && tab === 'dashboard' && <Dashboard session={session} profile={profile} onOpenMatch={setActiveMatch} />}
        {!showLogout && tab === 'admin' && isAdminRole && <Admin session={session} profile={profile} />}
      </div>

      <nav style={navStyle}>
        {navItems.map(item => (
          <button key={item.id}
            onClick={() => {
              if (item.id === 'account') { setShowLogout(true); setTab('fixtures') }
              else { setShowLogout(false); setTab(item.id) }
            }}
            style={{ ...navBtn, color: (item.id === 'account' ? showLogout : tab === item.id && !showLogout) ? '#185FA5' : '#888' }}>
            <span style={{ fontSize: '20px' }}>{item.icon}</span>
            <span style={{ fontSize: '10px', marginTop: '2px' }}>{item.label}</span>
          </button>
        ))}
      </nav>
    </div>
  )
}

const navStyle = { position: 'fixed', bottom: 0, left: '50%', transform: 'translateX(-50%)', width: '100%', maxWidth: '430px', display: 'flex', borderTop: '0.5px solid #e0e0e0', background: 'white', zIndex: 100 }
const navBtn = { flex: 1, padding: '10px 4px 8px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }
const inputStyle = { display: 'block', width: '100%', padding: '10px 12px', marginBottom: '10px', border: '1px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', background: 'white', boxSizing: 'border-box' }